import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { JUDGE_POSITIONS } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #524 — resolve the signed-in scribe's assignments across the (active org's)
// shows: the distinct { showId, show, ring, position } tuples where a class's
// scribeAssignments[position] includes the scribe's Cognito sub (#396). Used to
// auto-skip the show/ring picker when there is exactly one assignment.
//
// Returns { assignments, loading }. `assignments` is null until resolved.
export function useScribeAssignments(shows) {
  const api = useScoreApi();
  const { user } = useAuth();
  const sub = user?.profile?.sub ?? null;
  const [assignments, setAssignments] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait until the shows list is available.
    if (!shows) {
      setAssignments(null);
      setLoading(true);
      return undefined;
    }
    if (!sub) {
      setAssignments([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // #1961 — one batched call across every show instead of one
        // listClasses HTTP round trip per show: `shows` here is
        // useShows()'s full org-scoped list (every show the org has ever
        // run, unfiltered by date, see handlers/show.js), not just the
        // scribe's current assignments, so this fan-out grows with the
        // org's whole show history rather than staying small.
        const showsById = new Map(shows.map((show) => [show.id, show]));
        const classes = shows.length
          ? unwrap(
              await api.classes.listClassesForShows({
                showIds: shows.map((show) => show.id),
              })
            )
          : [];
        const tuples = [];
        for (const c of classes) {
          const show = showsById.get(c.showId);
          if (!show) continue;
          for (const position of JUDGE_POSITIONS) {
            const ids = c.scribeAssignments?.[position] ?? [];
            if (ids.includes(sub)) {
              tuples.push({
                showId: show.id,
                show,
                ring: c.ring ?? null,
                position,
              });
            }
          }
        }
        if (cancelled) return;
        // Dedupe by show + ring + position — many classes share an assignment.
        const seen = new Set();
        const distinct = [];
        for (const t of tuples) {
          const key = `${t.showId}|${t.ring}|${t.position}`;
          if (!seen.has(key)) {
            seen.add(key);
            distinct.push(t);
          }
        }
        setAssignments(distinct);
      } catch {
        if (!cancelled) setAssignments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, shows, sub]);

  return { assignments, loading };
}

// #627 — the session is just the show, so the picker can be skipped whenever all
// of a scribe's assignments live in ONE show (however many classes/positions
// those are — position is resolved per class). Returns that show, or null.
export function soleShow(assignments) {
  if (!assignments || assignments.length === 0) return null;
  const showIds = new Set(assignments.map((a) => a.showId));
  return showIds.size === 1 ? assignments[0].show : null;
}
